/**
 * OpenList 树形结构状态管理 Hook
 */

import { useState, useEffect, useCallback } from "react"
import { getFolderContentAction } from "@/lib/actions/openlist-actions"
import type { OpenListTreeNode, FolderContent } from "@/types/openlist"
import { 
  convertFolderContentToTreeNodes, 
  updateNodeChildren,
  saveExpandedFolders,
  loadExpandedFolders
} from "@/lib/utils/openlist-tree-utils"

interface UseOpenListTreeProps {
  subjectId: string
  initialPath?: string
}

export function useOpenListTree({ subjectId, initialPath = "/" }: UseOpenListTreeProps) {
  const [treeNodes, setTreeNodes] = useState<OpenListTreeNode[]>([])
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set([initialPath]))
  const [loadingNodes, setLoadingNodes] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)

  // 加载文件夹内容并更新树形结构
  const loadFolderContent = useCallback(async (folderPath: string) => {
    setLoadingNodes(prev => new Set(prev).add(folderPath))
    setError(null)
    
    try {
      const result = await getFolderContentAction(subjectId, folderPath)
      
      if (result.success && result.data) {
        updateTreeWithFolderContent(folderPath, result.data)
      } else {
        setError(result.error || "加载文件夹内容失败")
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "加载文件夹内容失败")
    } finally {
      setLoadingNodes(prev => {
        const newSet = new Set(prev)
        newSet.delete(folderPath)
        return newSet
      })
    }
  }, [subjectId])

  // 更新树形结构
  const updateTreeWithFolderContent = useCallback((folderPath: string, content: FolderContent) => {
    const newNodes = convertFolderContentToTreeNodes(folderPath, content)

    if (folderPath === '/') {
      // 根目录，直接设置树节点
      setTreeNodes(newNodes)
    } else {
      // 子目录，更新对应的父节点
      setTreeNodes(prevNodes => updateNodeChildren(prevNodes, folderPath, newNodes))
    }
  }, [])

  // 切换文件夹展开状态
  const toggleFolder = useCallback(async (node: OpenListTreeNode) => {
    if (node.type !== 'folder') return

    const isExpanded = expandedFolders.has(node.path)
    
    if (isExpanded) {
      // 折叠文件夹
      setExpandedFolders(prev => {
        const newSet = new Set(prev)
        newSet.delete(node.path)
        return newSet
      })
    } else {
      // 展开文件夹
      setExpandedFolders(prev => new Set(prev).add(node.path))
      
      // 如果还没有加载过内容，则加载
      if (!node.isLoaded && !loadingNodes.has(node.path)) {
        await loadFolderContent(node.path)
      }
    }
  }, [expandedFolders, loadingNodes, loadFolderContent])

  // 初始加载根目录
  useEffect(() => {
    loadFolderContent('/')
  }, [subjectId, loadFolderContent])

  // 保存展开状态到 localStorage
  useEffect(() => {
    if (subjectId) {
      saveExpandedFolders(subjectId, expandedFolders)
    }
  }, [expandedFolders, subjectId])

  // 从 localStorage 加载展开状态
  useEffect(() => {
    if (subjectId) {
      const storedExpanded = loadExpandedFolders(subjectId)
      setExpandedFolders(storedExpanded)
    }
  }, [subjectId])

  return {
    treeNodes,
    expandedFolders,
    loadingNodes,
    error,
    setError,
    toggleFolder,
    loadFolderContent
  }
}
